import { skipToken } from "@reduxjs/toolkit/query";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader, Table } from "shared/ui";
import { useGetSubmissionsQuery } from "features/submit-code";
import { useDuelTaskSelection, useGetDuelQuery } from "entities/duel";
import { selectCurrentUser } from "entities/user";
import { isAdminAccessToken, selectAuthToken } from "features/auth";
import { useAppSelector } from "shared/lib/storeHooks";
import { timestampMs } from "shared/lib/isoTimestamp";
import {
    shouldPollSubmissionStatus,
    useSubmissionStatusPolling,
} from "widgets/task-panel/lib/useSubmissionStatusPolling";
import { TaskSubmissionRow } from "../TaskSubmissionRow/TaskSubmissionRow";
import styles from "./TaskSubmissionsContent.module.scss";

export const TaskSubmissionsContent = () => {
    const { duelId } = useParams();
    const [searchParams] = useSearchParams();
    const currentUser = useAppSelector(selectCurrentUser);
    const token = useAppSelector(selectAuthToken);
    const isAdmin = isAdminAccessToken(token);
    const { data: duel, isLoading: isDuelLoading } = useGetDuelQuery(Number(duelId!), {
        skip: !duelId,
    });
    const isParticipant = (duel?.participants ?? []).some(
        (participant) => participant.id === currentUser?.id,
    );
    const isViewer = Boolean(duel && !isParticipant);
    const { selectedTaskKey } = useDuelTaskSelection(duel);
    const taskKeyFromQuery = searchParams.get("task");
    const resolvedTaskKey =
        taskKeyFromQuery ??
        selectedTaskKey ??
        (duel?.tasks ? Object.keys(duel.tasks).sort()[0] : null) ??
        (duel?.task_id ? "A" : null) ??
        "A";

    const submissionsArg = duelId
        ? {
              duelId,
              taskKey: resolvedTaskKey,
          }
        : skipToken;

    const {
        data: submissions,
        isLoading: isSubmissionsLoading,
        isError,
        refetch,
    } = useGetSubmissionsQuery(submissionsArg);
    const hasPendingSubmission =
        submissions?.some((submission) => shouldPollSubmissionStatus(submission.status)) ?? false;
    useSubmissionStatusPolling(hasPendingSubmission, refetch);

    if (isSubmissionsLoading || isDuelLoading) {
        return <Loader className={styles.centeredState} />;
    }

    if (isError || !submissions) {
        return <div>Ошибка при загрузке посылок</div>;
    }

    if (!duelId) {
        return <div>Ошибка: не указан ID дуэли</div>;
    }

    if (submissions.length === 0) {
        return (
            <div className={styles.centeredState}>
                <span className={styles.emptyStateText}>Посылок пока нет</span>
            </div>
        );
    }

    const sortedSubmissions = [...submissions].sort((a, b) => {
        const dateA = timestampMs(a.created_at) ?? 0;
        const dateB = timestampMs(b.created_at) ?? 0;
        return dateB - dateA;
    });

    return (
        <Table className={styles.submissionsTable}>
            <thead>
                <tr>
                    {isViewer && <th>Участник</th>}
                    <th>Статус</th>
                    <th>Язык</th>
                    <th>Время отправки</th>
                </tr>
            </thead>

            <tbody>
                {sortedSubmissions.map((submission) => (
                    <TaskSubmissionRow
                        key={submission.submission_id}
                        submission={submission}
                        duelId={duelId}
                        taskKey={resolvedTaskKey}
                        showAuthor={isViewer}
                        canOpenDetail={!isViewer || isAdmin}
                    />
                ))}
            </tbody>
        </Table>
    );
};
